import { createSlice } from '@reduxjs/toolkit';
import type { Project } from '~types/project';
import type {
  TimesheetEntry,
  TimesheetProposalQueryParams,
} from '../types/types.TimesheetProposals';
import { rangesEqual, type TimeRange } from '../utils/util.timesheetTransformers';

export interface TimesheetData {
  entries: TimesheetEntry[];
  unassignedTime: TimesheetEntry | null;
  // Note: isSubmitted is now derived from proposal status via RTK Query
}

/**
 * Context captured when a user triggers inline "Create project" from an AutoSheet
 * dropdown. Consumed by the saveProject thunk after the ProjectEditor modal saves,
 * to auto-assign the new project to the originating target.
 */
export type PendingProjectCreation =
  | { kind: 'row'; entryId: string }
  | {
      kind: 'claimRanges';
      proposalId: string;
      ranges: TimeRange[];
    }
  | { kind: 'batchReassign'; entryIds: string[] };

export interface TicTimesheetProposalState {
  isViewingTimesheet: boolean;
  timesheets: { [day: string]: TimesheetData };
  shouldGenerateTimesheet: boolean;
  generatingDates: { [date: string]: boolean };
  isAcceptingTimesheet: boolean;
  isRevertingTimesheet: boolean;
  regeneratingNotesDates: { [date: string]: boolean };
  regeneratingEntryIds: { [entryId: string]: boolean }; // Entry IDs currently being regenerated (supports concurrent regeneration)
  fullProjects: { [projectId: number]: Project };
  isLoadingSelectedProjects: boolean;
  selectedRanges: TimeRange[]; // Time ranges currently selected on the timeline (per-slot grain)
  selectedEntryIds: string[]; // Entry IDs that are currently selected for batch operations (delete, reassign project)
  flashingEntryIds: string[]; // Entry IDs that should flash after a claim or notes regeneration
  validationErrors: Record<string, Array<'project' | 'label'>>; // Validation errors for timesheet entries
  viewMode: 'day' | 'week'; // Day/Week view mode for the autosheet
  pendingProjectCreation: PendingProjectCreation | null;
  reopenProjectDropdownForEntryId: string | null; // signals the per-entry row to re-open its dropdown after inline project create
}

export const DEFAULT_QUERY_PARAMS: TimesheetProposalQueryParams = {
  interval_minutes: 15,
  provider: 'OpenAI',
  effort: 'low',
  note_language: 'English',
};

const initialState: TicTimesheetProposalState = {
  isViewingTimesheet: false,
  timesheets: {},
  shouldGenerateTimesheet: false,
  generatingDates: {},
  isAcceptingTimesheet: false,
  isRevertingTimesheet: false,
  regeneratingNotesDates: {},
  regeneratingEntryIds: {},
  fullProjects: {},
  isLoadingSelectedProjects: false,
  selectedRanges: [],
  selectedEntryIds: [],
  flashingEntryIds: [],
  validationErrors: {},
  viewMode: 'day',
  pendingProjectCreation: null,
  reopenProjectDropdownForEntryId: null,
};

const PROJECT_CLEAR_DRAFT_TYPE = 'timely/projects/CLEAR_DRAFT';

const ticTimesheetProposalSlice = createSlice({
  name: 'ticTimesheetProposals',
  initialState,
  reducers: {
    setIsViewingTimesheet: (state, action) => {
      state.isViewingTimesheet = action.payload;
    },
    setShouldGenerateTimesheet: (state, action) => {
      state.shouldGenerateTimesheet = action.payload;
    },
    setIsGeneratingForDate: (state, action) => {
      const { date, isGenerating } = action.payload;
      if (isGenerating) {
        state.generatingDates[date] = true;
      } else {
        delete state.generatingDates[date];
      }
    },
    clearAllGeneratingStates: state => {
      state.generatingDates = {};
    },
    setAcceptTimesheetLoading: (state, action) => {
      state.isAcceptingTimesheet = action.payload;
    },
    setRevertTimesheetLoading: (state, action) => {
      state.isRevertingTimesheet = action.payload;
    },
    setRegeneratingNotesForDate: (state, action) => {
      const { date, isRegenerating } = action.payload;
      if (isRegenerating) {
        state.regeneratingNotesDates[date] = true;
      } else {
        delete state.regeneratingNotesDates[date];
      }
    },
    setIsRegeneratingEntry: (
      state,
      action: { payload: { entryId: string; isRegenerating: boolean } }
    ) => {
      const { entryId, isRegenerating } = action.payload;
      if (isRegenerating) {
        state.regeneratingEntryIds[entryId] = true;
      } else {
        delete state.regeneratingEntryIds[entryId];
      }
    },
    clearAllRegeneratingEntries: state => {
      state.regeneratingEntryIds = {};
    },
    setFullProjects: (state, action) => {
      const projects = action.payload;
      projects.forEach((project: Project) => {
        state.fullProjects[project.id] = project;
      });
    },
    setIsLoadingSelectedProjects: (state, action) => {
      state.isLoadingSelectedProjects = action.payload;
    },
    toggleRangeSelection: (state, action: { payload: TimeRange }) => {
      const target = action.payload;
      const index = state.selectedRanges.findIndex(r => rangesEqual(r, target));
      if (index === -1) {
        state.selectedRanges.push(target);
      } else {
        state.selectedRanges.splice(index, 1);
      }
    },
    selectRanges: (state, action: { payload: TimeRange[] }) => {
      // Dedup against existing state and against earlier entries in the same
      // payload so a payload like [r1, r2, r1] adds only [r1, r2].
      const newRanges: TimeRange[] = [];
      for (const r of action.payload) {
        const inState = state.selectedRanges.some(s => rangesEqual(s, r));
        const inNew = newRanges.some(s => rangesEqual(s, r));
        if (!inState && !inNew) newRanges.push(r);
      }
      state.selectedRanges.push(...newRanges);
    },
    deselectRanges: (state, action: { payload: TimeRange[] }) => {
      const toRemove = action.payload;
      state.selectedRanges = state.selectedRanges.filter(
        r => !toRemove.some(t => rangesEqual(t, r))
      );
    },
    clearRangeSelection: state => {
      state.selectedRanges = [];
    },
    setRangeSelection: (state, action: { payload: TimeRange[] }) => {
      state.selectedRanges = action.payload;
    },
    toggleEntrySelection: (state, action: { payload: string }) => {
      const index = state.selectedEntryIds.indexOf(action.payload);
      if (index === -1) {
        state.selectedEntryIds.push(action.payload);
      } else {
        state.selectedEntryIds.splice(index, 1);
      }
    },
    setEntrySelection: (state, action: { payload: string[] }) => {
      state.selectedEntryIds = action.payload;
    },
    clearEntrySelection: state => {
      state.selectedEntryIds = [];
    },
    setFlashingEntryIds: (state, action: { payload: string[] }) => {
      state.flashingEntryIds = action.payload;
    },
    clearFlashingEntryIds: state => {
      state.flashingEntryIds = [];
    },
    setValidationErrors: (
      state,
      action: { payload: Record<string, Array<'project' | 'label'>> }
    ) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: state => {
      state.validationErrors = {};
    },
    setViewMode: (state, action: { payload: 'day' | 'week' }) => {
      state.viewMode = action.payload;
    },
    setPendingProjectCreation: (state, action: { payload: PendingProjectCreation | null }) => {
      state.pendingProjectCreation = action.payload;
    },
    setReopenProjectDropdownForEntryId: (state, action: { payload: string | null }) => {
      state.reopenProjectDropdownForEntryId = action.payload;
    },
  },
  extraReducers: builder => {
    // When the ProjectEditor modal is cancelled or otherwise cleared, drop any
    // pending inline-create context so a stale entryId doesn't leak into a later save.
    builder.addMatcher(
      (action): action is { type: typeof PROJECT_CLEAR_DRAFT_TYPE } =>
        action.type === PROJECT_CLEAR_DRAFT_TYPE,
      state => {
        state.pendingProjectCreation = null;
      }
    );
  },
  selectors: {
    getIsViewingTimesheet: state => state.isViewingTimesheet,
    getShouldGenerateTimesheet: state => state.shouldGenerateTimesheet,
    getIsGeneratingForDate: (state, date: string) => Boolean(state.generatingDates[date]),
    getIsAcceptingTimesheet: state => state.isAcceptingTimesheet,
    getIsRevertingTimesheet: state => state.isRevertingTimesheet,
    getIsRegeneratingNotesForDate: (state, date: string) =>
      Boolean(state.regeneratingNotesDates[date]),
    getIsRegeneratingEntry: (state, entryId: string) =>
      Boolean(state.regeneratingEntryIds[entryId]),
    getRegeneratingEntryIds: state => state.regeneratingEntryIds,
    getFullProjects: state => state.fullProjects,
    getIsLoadingSelectedProjects: state => state.isLoadingSelectedProjects,
    getSelectedRanges: state => state.selectedRanges,
    getSelectedRangeCount: state => state.selectedRanges.length,
    isRangeSelected: (state, range: TimeRange) =>
      state.selectedRanges.some(r => rangesEqual(r, range)),
    getSelectedEntryIds: state => state.selectedEntryIds,
    getSelectedEntryCount: state => state.selectedEntryIds.length,
    isEntrySelected: (state, entryId: string) => state.selectedEntryIds.includes(entryId),
    getFlashingEntryIds: state => state.flashingEntryIds,
    getValidationErrors: state => state.validationErrors,
    getViewMode: state => state.viewMode,
    getPendingProjectCreation: state => state.pendingProjectCreation,
    getReopenProjectDropdownForEntryId: state => state.reopenProjectDropdownForEntryId,
  },
});

export const {
  setIsViewingTimesheet,
  setShouldGenerateTimesheet,
  setIsGeneratingForDate,
  clearAllGeneratingStates,
  setAcceptTimesheetLoading,
  setRevertTimesheetLoading,
  setRegeneratingNotesForDate,
  setIsRegeneratingEntry,
  clearAllRegeneratingEntries,
  setFullProjects,
  setIsLoadingSelectedProjects,
  toggleRangeSelection,
  selectRanges,
  deselectRanges,
  clearRangeSelection,
  setRangeSelection,
  toggleEntrySelection,
  setEntrySelection,
  clearEntrySelection,
  setFlashingEntryIds,
  clearFlashingEntryIds,
  setValidationErrors,
  clearValidationErrors,
  setViewMode,
  setPendingProjectCreation,
  setReopenProjectDropdownForEntryId,
} = ticTimesheetProposalSlice.actions;
export const {
  getIsViewingTimesheet,
  getShouldGenerateTimesheet,
  getIsGeneratingForDate,
  getIsAcceptingTimesheet,
  getIsRevertingTimesheet,
  getIsRegeneratingNotesForDate,
  getIsRegeneratingEntry,
  getRegeneratingEntryIds,
  getFullProjects,
  getIsLoadingSelectedProjects,
  getSelectedRanges,
  getSelectedRangeCount,
  isRangeSelected,
  getSelectedEntryIds,
  getSelectedEntryCount,
  isEntrySelected,
  getFlashingEntryIds,
  getValidationErrors,
  getViewMode,
  getPendingProjectCreation,
  getReopenProjectDropdownForEntryId,
} = ticTimesheetProposalSlice.selectors;

export default ticTimesheetProposalSlice.reducer;
